import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { usePrompt } from "@/components/ui/confirm-dialog";
import { cn } from "@/lib/utils";

interface SubScoresEditorProps {
  /** Per-rule cap — caps every sub_score input. */
  max: number;
  subScores: Record<string, number>;
  onUpdate: (key: string, points: number) => void;
  onAdd?: (key: string, points: number) => void;
  onRemove?: (key: string) => void;
  /** When true, the operator can append new sub-score entries
   *  (e.g. a new metadata field on `signaux_recrutement`) and
   *  remove existing ones. The fixed-list rules (origine_fondateur)
   *  stay false — the categories are seeded by the scoring engine. */
  allowAddRemove?: boolean;
  /** Hint shown at the top right (e.g. "Max: 15pts"). */
  hint?: string;
  /** Footer copy explaining how the sub_scores are evaluated.
   *  Defaults to a generic "first-match-wins" line. */
  footerHint?: string;
  /** Format the human label for a sub_score key. Defaults to a
   *  snake_case → Title Case conversion that strips a few common
   *  prefixes. */
  labelFor?: (key: string) => string;
}

/**
 * Generic sub_scores editor — renders one input per entry in the
 * `subScores` map, sorted descending by points (priority order),
 * with an inline points input and a visual progress bar capped at
 * `max`.
 *
 * Used by `signaux_recrutement` with `allowAddRemove` so an admin
 * can introduce a new boolean signal (e.g. is_hiring_pmm) without
 * a code change. `origine_fondateur` keeps its bespoke editor
 * (`FounderOriginSubScores`) because the excluded-origin marking
 * doesn't generalise here.
 */
export function SubScoresEditor({
  max,
  subScores,
  onUpdate,
  onAdd,
  onRemove,
  allowAddRemove = false,
  hint,
  footerHint = "Sub-scores are evaluated in descending point order, first match wins. Higher points = higher priority.",
  labelFor = defaultLabelFor,
}: SubScoresEditorProps) {
  const prompt = usePrompt();
  const entries = Object.entries(subScores)
    .map(([k, v]) => [k, Number(v) || 0] as const)
    .sort((a, b) => b[1] - a[1]);

  async function handleAdd() {
    if (!onAdd) return;
    const key = await prompt({
      title: "Add signal",
      description: "Enter the metadata key for the new sub-score.",
      placeholder: "is_hiring_pmm",
      confirmLabel: "Add",
    });
    if (!key) return;
    if (subScores[key] != null) {
      toast.error(`"${key}" is already in this rule's sub-scores.`);
      return;
    }
    onAdd(key, 0);
  }

  return (
    <div className="mt-3 rounded-xl border border-slate-900/[0.06] bg-slate-50/40 p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
          Sub-scores
        </p>
        <p className="text-[11px] text-slate-400">
          {hint ?? (
            <>
              0 –{" "}
              <span className="font-mono tabular-nums text-slate-600">
                {max}
              </span>{" "}
              pts
            </>
          )}
        </p>
      </div>
      <ul className="space-y-1.5">
        {entries.map(([key, points]) => {
          const pct = max > 0 ? Math.min(100, (points / max) * 100) : 0;
          return (
            <li
              key={key}
              className="group flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-white"
            >
              <span className="min-w-0 flex-1 truncate text-sm text-slate-700">
                {labelFor(key)}
              </span>
              <span className="hidden shrink-0 font-mono text-[10px] text-slate-400 group-hover:inline">
                {key}
              </span>
              <div className="flex shrink-0 items-center gap-1">
                <Input
                  type="number"
                  min={0}
                  max={max}
                  value={points}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (!Number.isFinite(n)) return;
                    onUpdate(key, n);
                  }}
                  className="h-8 w-16 rounded-lg border-slate-200 text-right font-mono text-xs"
                />
                <span className="text-[10px] text-slate-400">pts</span>
              </div>
              {/* Visual progress bar — gives a quick read on
               *  relative weights without forcing the operator to
               *  read every number. */}
              <div className="hidden h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-slate-200 sm:block">
                <div
                  className={cn(
                    "h-full rounded-full bg-indigo-500 transition-all",
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {allowAddRemove && onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(key)}
                  aria-label={`Remove ${labelFor(key)}`}
                  className="text-slate-300 opacity-0 transition-opacity hover:text-rose-500 group-hover:opacity-100"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {allowAddRemove && onAdd && (
        <button
          type="button"
          onClick={handleAdd}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md text-xs text-slate-500 transition-colors hover:text-indigo-600"
        >
          <Plus className="h-3.5 w-3.5" />
          Add signal
        </button>
      )}
      <p className="mt-3 text-[10px] text-slate-400">{footerHint}</p>
    </div>
  );
}

function defaultLabelFor(key: string): string {
  // Curated names for the well-known signals; everything else
  // falls back to a snake_case → Title Case conversion that strips
  // the common is_hiring_ prefix so the UI label reads "Hiring X"
  // rather than "Is Hiring X".
  const MAP: Record<string, string> = {
    is_hiring_finance: "Hiring Finance",
    is_hiring_cos_coo: "Hiring CoS/COO",
    is_hiring_hr_admin: "Hiring HR/Admin",
    headcount_growth_rate: "Headcount Growth",
  };
  if (MAP[key]) return MAP[key];
  return key
    .replace(/^is_hiring_/, "Hiring ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
